// Dashboard.tsx

import { useState, useMemo, useEffect } from 'react';
import { InventoryRecord } from '../types/pocketbase-types.ts';
import { AppShell } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import InventoryTable from '../components/InventoryTable';
import EditInventoryModal from '../components/EditInventoryModal';
import FilterNavbar from '../components/FilterNavbar';
import HeaderBar from '../components/HeaderBar';
import { useInventoryUrlState } from '../hooks/routing.hooks';
import { TABLE_CONSTANTS } from '../constants/table.tsx';
import {
  useGetInventory,
  useGetCategoryRecords,
  useGetManufacturers,
  useGetMinMaxPrices,
  // useSubscription,
  useDeleteItem,
  useUpdateItem,
  useCreateItem,
} from '../hooks/database.hooks.tsx';

export default function Dashboard() {
  // useSubscription('inventory');
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

  // Local state for filters before applying
  const [localSelectedCategories, setLocalSelectedCategories] = useState<
    string[]
  >([]);
  const [localSelectedManufacturers, setLocalSelectedManufacturers] = useState<
    string[]
  >([]);
  const [localSelectedCurrencyTypes, setLocalSelectedCurrencyTypes] = useState<{
    selling: string[];
    purchase: string[];
    estimated: string[];
  }>({
    selling: [],
    purchase: [],
    estimated: [],
  });
  const [localSelectedCurrencies, setLocalSelectedCurrencies] = useState<
    string[]
  >([]);
  const [localPriceRange, setLocalPriceRange] = useState<
    [number | null, number | null] | null
  >(null);

  // URL State
  const {
    page,
    query,
    nameQuery,
    selectedCategories,
    selectedManufacturers,
    selectedCurrencyTypes,
    globalCurrency,
    priceRange,
    sortStatus,
    setPage,
    setQuery,
    setNameQuery,
    setSelectedCategories,
    setSelectedManufacturers,
    setSelectedCurrencyType,
    setGlobalCurrency,
    setPriceRange,
    setSortStatus,
    resetFilters,
  } = useInventoryUrlState();

  const [localQuery, setLocalQuery] = useState(query);
  const [debouncedQuery] = useDebouncedValue(localQuery, 500);

  // Update URL state when debounced query changes
  useEffect(() => {
    if (debouncedQuery !== query) {
      setQuery(debouncedQuery);
      setPage(1);
    }
  }, [debouncedQuery]);

  // Sync local state with URL state
  useEffect(() => {
    if (query !== localQuery && query !== debouncedQuery) {
      setLocalQuery(query);
    }
  }, [query]);

  // const [debouncedNameQuery] = useDebouncedValue(nameQuery, 1000);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  let navbarResetFn: () => void;

  // Queries
  const { data: minMaxPrices } = useGetMinMaxPrices();
  const categoriesQuery = useGetCategoryRecords();
  const manufacturersQuery = useGetManufacturers();
  const deleteItemMutation = useDeleteItem();
  const updateItemMutation = useUpdateItem();
  const createItemMutation = useCreateItem();
  const inventoryQuery = useGetInventory(
    'inventory',
    page,
    TABLE_CONSTANTS.PAGE_SIZE,
    sortStatus.columnAccessor as keyof InventoryRecord,
    sortStatus.direction,
    {
      query: query,
      nameQuery: nameQuery,
      categories: selectedCategories,
      manufacturers: selectedManufacturers,
      currencies: {
        sellingPrice: selectedCurrencyTypes.selling,
        purchasePrice: selectedCurrencyTypes.purchase,
        estimatedSellingPrice: selectedCurrencyTypes.estimated,
      },
      globalCurrency,
      priceRange: priceRange
        ? [
            priceRange[0] === null ? null : priceRange[0],
            priceRange[1] === null ? null : priceRange[1],
          ]
        : undefined,
    }
  );

  const clearFiltersDisabled = useMemo(
    () =>
      selectedCategories.length === 0 &&
      selectedManufacturers.length === 0 &&
      selectedCurrencyTypes.selling.length === 0 &&
      selectedCurrencyTypes.purchase.length === 0 &&
      selectedCurrencyTypes.estimated.length === 0 &&
      !priceRange &&
      !query &&
      !nameQuery,
    [
      selectedCategories,
      selectedManufacturers,
      selectedCurrencyTypes,
      priceRange,
      query,
      nameQuery,
    ]
  );

  const handleClearFilters = () => {
    console.log('onClearFilters called');
    console.log('Clear filters clicked');
    resetFilters();
    console.log('After resetFilters');
    navbarResetFn?.();
    console.log('After navbarResetFn');
  };

  const handleApplyFilters = () => {
    setSelectedCategories(localSelectedCategories);
    setSelectedManufacturers(localSelectedManufacturers);
    setSelectedCurrencyType('selling', localSelectedCurrencies);
    setSelectedCurrencyType('purchase', localSelectedCurrencies);
    setSelectedCurrencyType('estimated', localSelectedCurrencies);
    setPriceRange(localPriceRange);
    setPage(1);
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 300,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="0"
      withBorder={false}
      transitionDuration={0}
    >
      <AppShell.Header>
        <HeaderBar
          mobileOpened={mobileOpened}
          toggleMobile={toggleMobile}
          desktopOpened={desktopOpened}
          toggleDesktop={toggleDesktop}
          query={query}
          setQuery={setQuery}
          setPage={setPage}
          onClearFilters={handleClearFilters}
          clearFiltersDisabled={clearFiltersDisabled}
          onCreateNew={() => setIsCreateModalOpen(true)}
        />
      </AppShell.Header>

      <AppShell.Navbar>
        <FilterNavbar
          initialNameQuery={nameQuery}
          onNameQueryChange={setNameQuery}
          localSelectedCategories={localSelectedCategories}
          setLocalSelectedCategories={setLocalSelectedCategories}
          localSelectedManufacturers={localSelectedManufacturers}
          setLocalSelectedManufacturers={setLocalSelectedManufacturers}
          localSelectedCurrencies={localSelectedCurrencies}
          setLocalSelectedCurrencies={setLocalSelectedCurrencies}
          localSelectedCurrencyTypes={localSelectedCurrencyTypes}
          setLocalSelectedCurrencyTypes={setLocalSelectedCurrencyTypes}
          localPriceRange={localPriceRange}
          setLocalPriceRange={setLocalPriceRange}
          categoriesQuery={categoriesQuery}
          manufacturersQuery={manufacturersQuery}
          minMaxPrices={minMaxPrices}
          onApplyFilters={handleApplyFilters}
          resetLocalState={(fn) => (navbarResetFn = fn)}
          globalCurrency={globalCurrency}
          setGlobalCurrency={setGlobalCurrency}
        />
      </AppShell.Navbar>

      <AppShell.Main>
        <InventoryTable
          inventoryQuery={inventoryQuery}
          categoriesQuery={categoriesQuery}
          manufacturersQuery={manufacturersQuery}
          minMaxPrices={minMaxPrices?.data}
          deleteItemMutation={deleteItemMutation}
          updateItemMutation={updateItemMutation}
          page={page}
          setPage={setPage}
          sortStatus={sortStatus}
          setSortStatus={setSortStatus}
          nameQuery={nameQuery}
          setNameQuery={setNameQuery}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          selectedManufacturers={selectedManufacturers}
          setSelectedManufacturers={setSelectedManufacturers}
          selectedCurrencyTypes={selectedCurrencyTypes}
          setSelectedCurrencyTypes={setSelectedCurrencyType}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
        />
      </AppShell.Main>

      <EditInventoryModal
        mode="create"
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={async (values) => {
          await createItemMutation.mutateAsync(values);
          setIsCreateModalOpen(false);
        }}
        categories={categoriesQuery.data || []}
        manufacturers={manufacturersQuery.data || []}
      />
    </AppShell>
  );
}
